import React from "react";
import {
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  CustomInput,
} from "reactstrap";
import { AddCommaNumber } from "../../../helpers/Utils";
import moment from "moment";
import classnames from "classnames";

const getInitials = (name) => {
  if (!name) return "";
  const parts = name.trim().split(" ");
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const getAvatarBg = (name) => {
  if (!name) return "#8f9bb3";
  const colors = [
    "#a180ff", // purple/indigo
    "#ffaa00", // orange
    "#00d68f", // green
    "#ff3d71", // red
    "#3366ff", // blue
    "#00d6d6", // teal
    "#ff7f50", // coral
  ];
  let sum = 0;
  for (let i = 0; i < name.length; i++) {
    sum += name.charCodeAt(i);
  }
  return colors[sum % colors.length];
};

const getCategoryBadgeClass = (catName) => {
  if (!catName) return "";
  const cat = catName.toLowerCase();
  if (cat.includes("uống") || cat.includes("nước")) {
    return { bg: "rgba(51, 102, 255, 0.1)", color: "#3366ff" };
  } else if (cat.includes("hạt") || cat.includes("nguyên liệu")) {
    return { bg: "rgba(143, 155, 179, 0.1)", color: "#8f9bb3" };
  } else if (cat.includes("quả") || cat.includes("trái cây")) {
    return { bg: "rgba(0, 214, 143, 0.1)", color: "#00d68f" };
  } else if (cat.includes("siro") || cat.includes("topping")) {
    return { bg: "rgba(143, 0, 255, 0.1)", color: "#8f00ff" };
  } else if (cat.includes("tạp hóa")) {
    return { bg: "rgba(165, 42, 42, 0.1)", color: "#a52a2a" };
  } else if (cat.includes("trà") || cat.includes("cà phê")) {
    return { bg: "rgba(255, 127, 80, 0.1)", color: "#ff7f50" };
  } else if (cat.includes("sữa") || cat.includes("kem")) {
    return { bg: "rgba(0, 214, 214, 0.1)", color: "#00d6d6" };
  }
  return { bg: "rgba(143, 155, 179, 0.1)", color: "#8f9bb3" };
};

const ProviderDataTable = ({
  data = [],
  selectedItems = [],
  onChangeCheckbox,
  onSelectAll,
  onClickedItem,
  onClickedUpdate,
  onClickedDelete,
  toggleItemLogFunc,
  selectedProviderId = null,
}) => {
  const isAllSelected = data.length > 0 && selectedItems.length >= data.length;

  return (
    <div className="custom-provider-table-wrapper">
      <table className="provider-table">
        <colgroup>
          <col className="provider-col-select" />
          <col className="provider-col-name" />
          <col className="provider-col-contact" />
          <col className="provider-col-category" />
          <col className="provider-col-money" />
          <col className="provider-col-money" />
          <col className="provider-col-date" />
          <col className="provider-col-status" />
          <col className="provider-col-action" />
        </colgroup>
        <thead>
          <tr>
            <th className="provider-table-center">
              <CustomInput
                className="custom-checkbox"
                type="checkbox"
                id="checkAll"
                label=""
                checked={isAllSelected}
                onChange={() => onSelectAll(true)}
              />
            </th>
            <th>Nhà cung cấp</th>
            <th>Liên hệ</th>
            <th>Nhóm hàng</th>
            <th>Công nợ</th>
            <th>Tổng nhập</th>
            <th>Giao dịch gần nhất</th>
            <th>Trạng thái</th>
            <th className="provider-table-center" />
          </tr>
        </thead>
        <tbody>
          {data.map((item) => {
            const hasDebt = parseFloat(item.total_price_pending) > 0;
            const totalImport = (item.company_provider_item || []).reduce(
              (acc, curr) => acc + (parseFloat(curr.price_income) || 0),
              0
            );

            // Determine Group/Category based on first item
            const firstItemName =
              item.company_provider_item && item.company_provider_item.length > 0
                ? item.company_provider_item[0].name
                : "Chưa phân loại";

            const catBadgeStyle = getCategoryBadgeClass(firstItemName);

            // Get last transaction date
            const lastImportDate =
              item.company_provider_item && item.company_provider_item.length > 0
                ? moment(
                    Math.max(
                      ...item.company_provider_item.map((i) =>
                        new Date(i.createdAt).getTime()
                      )
                    )
                  ).format("DD/MM/YYYY")
                : "-";

            const isRowSelected = selectedProviderId === item.id;

            return (
              <tr
                key={item.id}
                className={classnames({
                  selected: isRowSelected || selectedItems.includes(item.id),
                })}
                onClick={() => onClickedItem(item)}
                style={{ cursor: "pointer" }}
              >
                <td
                  style={{ textAlign: "center" }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <CustomInput
                    className="custom-checkbox"
                    type="checkbox"
                    id={`check_${item.id}`}
                    label=""
                    checked={selectedItems.includes(item.id)}
                    onChange={() => onChangeCheckbox(item.id)}
                  />
                </td>
                <td>
                  <div className="provider-info-cell">
                    <div
                      className="provider-avatar"
                      style={{ backgroundColor: getAvatarBg(item.name) }}
                    >
                      {getInitials(item.name)}
                    </div>
                    <span className="provider-name" title={item.name}>
                      {item.name}
                    </span>
                  </div>
                </td>
                <td className="provider-table-nowrap" title={item.phone || "-"}>
                  {item.phone || "-"}
                </td>
                <td>
                  <span
                    className="category-badge"
                    title={firstItemName}
                    style={{
                      backgroundColor: catBadgeStyle.bg,
                      color: catBadgeStyle.color,
                    }}
                  >
                    {firstItemName}
                  </span>
                </td>
                <td
                  className={classnames("debt-amount", {
                    "text-danger": hasDebt,
                  })}
                >
                  {AddCommaNumber(item.total_price_pending)}đ
                </td>
                <td className="provider-table-nowrap">{AddCommaNumber(totalImport)}đ</td>
                <td className="provider-table-nowrap">{lastImportDate}</td>
                <td>
                  <span
                    className={classnames("status-badge", {
                      paid: !hasDebt && totalImport > 0,
                      debt: hasDebt,
                    })}
                  >
                    {!hasDebt && totalImport > 0 ? "Đã trả hết" : hasDebt ? "Còn nợ" : "Chưa giao dịch"}
                  </span>
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                  <UncontrolledDropdown>
                    <DropdownToggle tag="button" className="btn-actions-dropdown">
                      <i className="simple-icon-options-vertical" />
                    </DropdownToggle>
                    <DropdownMenu right>
                      {/* <DropdownItem onClick={() => onClickedItem(item)}>
                        Xem chi tiết
                      </DropdownItem> */}
                      <DropdownItem onClick={() => onClickedUpdate(item)}>
                        Chỉnh sửa
                      </DropdownItem>
                      {hasDebt && (
                        <DropdownItem
                          onClick={() => {
                            const unpaidItem = item.company_provider_item.find(
                              (i) => (parseFloat(i.price_income) || 0) - (parseFloat(i.price_outcome) || 0) > 0
                            );
                            if (unpaidItem) {
                              toggleItemLogFunc(unpaidItem);
                            }
                          }}
                        >
                          Thanh toán nợ
                        </DropdownItem>
                      )}
                      <DropdownItem divider />
                      <DropdownItem
                        onClick={() => onClickedDelete(item)}
                        className="text-danger"
                      >
                        Xóa
                      </DropdownItem>
                    </DropdownMenu>
                  </UncontrolledDropdown>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default React.memo(ProviderDataTable);
